import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import TextFieldCore from '@adsk/bim360-matrix-react-text-field-core';
import InlineEditTextField from '@adsk/bim360-matrix-react-inline-edit-text-field';
import FloatingLabelTextField from '@adsk/bim360-matrix-react-floating-label-text-field';
import InputValidation from '@adsk/bim360-matrix-react-input-validation';
import InputValidationView from '@adsk/bim360-matrix-react-input-validation-view';
import Editable from '@adsk/bim360-matrix-react-editable';

import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';
import { validate, createRequiredValidator } from '@adsk/bim360-matrix-react-helpers/es/validate';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';

export const ValidationTypes = InputValidation.Types;
export const Behaviors = TextFieldCore.Behaviors;
export const DefaultBehaviors = TextFieldCore.DefaultBehaviors;
export const InputUITypes = {
  NORMAL: 'NORMAL',
  FLOATING_LABEL: 'FLOATING_LABEL',
  INLINE_EDIT: 'INLINE_EDIT'
};

/**
 * Returns an object with style & className that merges them from the `specificProps` & `props`
 */
const getStylingProps = (props, specificProps, elementName) => {
  // We only want one className for the component so this is the precedence order:
  // 1. Specific className i.e. input={{ className: 'MyClassNameForInput' }}
  // 2. props className -> className='MyClassName'
  // 3. Default className -> 'TextInput'
  // For cases 2 & 3 we want to append the element name to the class name since it's not specific to that element.
  return {
    style: { ...props.style, ...specificProps.style },
    className: specificProps.className || `${props.classNamePrefix}__${elementName}`
  };
};

const getInputConfiguration = inputUIType => {
  switch (inputUIType) {
    case InputUITypes.NORMAL:
      return {
        component: TextFieldCore,
        editableCloseEvent: 'onBlur'
      };
    case InputUITypes.FLOATING_LABEL:
      return {
        component: FloatingLabelTextField,
        editableCloseEvent: 'onBlur'
      };
    case InputUITypes.INLINE_EDIT:
      return {
        component: InlineEditTextField,
        editableCloseEvent: 'onInlineEditEnd'
      };
    default:
      throw new Error(
        `You used '${inputUIType}' as the value for 'inputUIType' but it's not a valid one, please use one of InputUIType[${Object.keys(
          InputUITypes
        ).join('|')}]`
      );
  }
};

/**
 * Takes the props specified in `valueKeys` from either `inputProps` or `props` if they don't exist in `specificProps`.
 * This assumes that the values in valueKeys contain only keys that don't have meaning for `null` or `undefined` values.
 */
const mergeProps = (props, inputProps, specificProps, valueKeys) => {
  const partialProps = valueKeys.reduce((newProps, key) => {
    if (isNullOrUndefined(specificProps[key])) {
      newProps[key] = isNullOrUndefined(inputProps[key]) ? props[key] : inputProps[key];
    } else {
      newProps[key] = specificProps[key];
    }
    return newProps;
  }, {});

  return { ...specificProps, ...partialProps };
};

/**
 * @class TextInput - The one text component to unite them all!
 * Has validation, editability(?), floating labels, inline editing, and if you really want just a normal text input that's also ok.
 *
 * Some components may have additional api using sass mixins - refer to TextFieldCore, InlineEditTextField & FloatingLabelTextField for more information.
 *
 * Note: This class exists for reffing purposes only.
 */
class TextInput extends PureComponent {
  constructor(props) {
    super(props);
    this.focus = this.focus.bind(this);
    this.blur = this.blur.bind(this);
    this.select = this.select.bind(this);
    this.setInputComponentRef = this.setInputComponentRef.bind(this);
    this.onChangeInput = this.onChangeInput.bind(this);
  }

  setInputComponentRef(ref) {
    this.inputComponentRef = ref;
  }

  onChangeInput(value) {
    const {
      inputProps: { onChange: inputPropsOnChange = () => {} } = {},
      onChange = inputPropsOnChange,
      validationProps: { validator: validators, message, required: validationPropsRequired } = {},
      required = validationPropsRequired
    } = this.props;

    const valid = message
      ? false
      : !validate(value, {
        validators,
        requiredValidator: createRequiredValidator(required, 'REQUIRED')
      });

    return onChange(value, { valid });
  }

  render() {
    const { editableProps, inputProps = {}, validationProps, inputUIType } = this.props;
    const inputConfig = getInputConfiguration(inputUIType);

    const renderInput = extraProps => {
      const InputComponent = inputConfig.component;
      return (
        <InputComponent
          ref={this.setInputComponentRef}
          {...mergeProps(this.props, inputProps, inputProps, [
            'disabled',
            'placeholder',
            'value',
            'onBlur',
            'onFocus'
          ])}
          {...extraProps}
          onChange={this.onChangeInput}
          {...getStylingProps(this.props, inputProps, 'text-field')}
        />
      );
    };

    const renderEditable = extraInputProps => {
      return (
        <Editable
          {...extraInputProps}
          {...mergeProps(this.props, inputProps, editableProps, [
            'disabled',
            'placeholder',
            'value'
          ])}
          {...getStylingProps(this.props, editableProps, 'editable')}
        >
          {closeEditable => {
            return renderInput({
              ...extraInputProps,
              [inputConfig.editableCloseEvent]: (...args) => {
                closeEditable();
                if (inputProps[inputConfig.editableCloseEvent]) {
                  return inputProps[inputConfig.editableCloseEvent](...args);
                }

                return undefined;
              },
              autoselect: true
            });
          }}
        </Editable>
      );
    };

    const renderValidationView = (renderInner, stylingProps) => {
      return (
        <InputValidationView {...validationProps} {...stylingProps}>
          {renderInner({ valid: false })}
        </InputValidationView>
      );
    };

    const renderInputValidation = (renderInner, stylingProps) => {
      return (
        <InputValidation
          {...mergeProps(this.props, inputProps, validationProps, ['required', 'value'])}
          {...stylingProps}
        >
          {valid => renderInner({ valid })}
        </InputValidation>
      );
    };

    const renderInner = editableProps ? renderEditable : renderInput;

    if (validationProps || 'required' in this.props) {
      const stylingProps = getStylingProps(this.props, validationProps, 'validation');

      return validationProps.message
        ? renderValidationView(renderInner, stylingProps)
        : renderInputValidation(renderInner, stylingProps);
    } else {
      return renderInner();
    }
  }

  focus() {
    this.inputComponentRef && this.inputComponentRef.focus();
  }

  blur() {
    this.inputComponentRef && this.inputComponentRef.blur();
  }

  select() {
    this.inputComponentRef && this.inputComponentRef.select();
  }
}

TextInput.defaultProps = {
  classNamePrefix: 'TextInput',
  inputUIType: InputUITypes.NORMAL
};

TextInput.propTypes = {
  /**
   * Props to configure the input more specifically
   * Should depend on the inputUIType
   */
  inputProps: PropTypes.oneOfType([
    PropTypes.shape(TextFieldCore.propTypes),
    PropTypes.shape(InlineEditTextField.propTypes),
    PropTypes.shape(FloatingLabelTextField.propTypes)
  ]),
  /**
   * Props to configure whether or not the component should look editable and the view when not in edit mode.
   */
  editableProps: PropTypes.shape(Editable.propTypes),
  /**
   * Props to configure the validation for the input.
   * If a message is passed it is considered to just view the
   */
  validationProps: PropTypes.oneOfType([
    PropTypes.shape(InputValidation.propTypes),
    PropTypes.shape(InputValidationView.propTypes),
    PropTypes.shape({
      /**
       * Validation error message if the value is invalid.
       */
      message: PropTypes.string
    })
  ]),
  /**
   * The ui type of the input - normal, inline edit or floating.
   */
  inputUIType: PropTypes.oneOf(objectValues(InputUITypes)).isRequired,
  /**
   * The value for the input.
   * Will not override inputProps.value, editableProps.value or validationProps.value if one of them is passed.
   */
  value: PropTypes.string,
  /**
   * The placeholder for the input.
   * * Will not override inputProps.placeholder or editableProps.placeholder if one of them is passed.
   */
  placeholder: PropTypes.string,
  /**
   * DEPRECATED: use `classNamePrefix` instead
   */
  className: PropTypes.string,
  /**
   * The className prefix for the input.
   * Will generate `${classNamePrefix}__(text-field | editable | validation) for inner elements depends on the element's type.
   * Will not override inputProps.className, editableProps.className or validationProps.className if one of them is passed.
   */
  classNamePrefix: PropTypes.string,
  /**
   * The style for the input.
   * Will not override inputProps.style, editableProps.style or validationProps.style if one of them is passed.
   * This applies to all of them - use with caution!
   * If you want different style that applies to each of them do inputProps.style, editableProps.style or validationProps.style.
   */
  style: PropTypes.object,
  /**
   * Called whenever the value in the input has changed - convenience for inputProps.onChange
   */
  onChange: PropTypes.func,
  /**
   * Called whenever the input field receives focus - convenience for inputProps.onFocus
   */
  onFocus: PropTypes.func,
  /**
   * Called whenever the input field is blurred - convenience for inputProps.onBlur
   */
  onBlur: PropTypes.func,
  /**
   * whether or not the input is disabled.
   * Will not override inputProps.disabled or editableProps.disabled if one of them is passed.
   */
  disabled: PropTypes.bool,
  /**
   * whether or not the input is required.
   * Will not override inputProps.required or validationProps.required if one of them is passed.
   */
  required: PropTypes.bool
};

export default TextInput;
